import { useState, useRef, useEffect } from 'react';
import { PhoneInputField } from '../UISystem/Forms/PhoneInputField';
import { ButtonRounded } from '../UISystem/Buttons/ButtonRounded';
import {
  sendOtp,
  verifyOtp,
  hydrateFromAuth,
  INVITE_REQUIRED_MESSAGE,
} from '../../services/auth/otpAuth';
import { logEvent } from '../../services/analytics/eventLogger';
import { COLORS, TYPOGRAPHY } from '../../styles/UISystem';
import { CROWN_BLACK } from '../../styles/brandAssets';

/**
 * PWAAuthScreen — Full-screen OTP auth for PWA context
 *
 * Four stages:
 * 1. Phone input — enter phone number, send OTP
 * 2. Code entry — six digit inputs, verify OTP
 * 3. Loading — hydrating data from Supabase
 * 4. Error — with retry actions
 */

const STAGES = {
  PHONE: 'phone',
  CODE: 'code',
  LOADING: 'loading',
  ERROR: 'error',
};

const PWAAuthScreen = () => {
  const [stage, setStage] = useState(STAGES.PHONE);
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [kickedMessage] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('kicked') === 'true') {
      window.history.replaceState({}, '', window.location.pathname);
      return true;
    }
    return false;
  });

  const codeInputRefs = useRef([]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // A login refused with the invite message — at send (no SMS) or after the code
  const logInviteRefusal = (stage) =>
    logEvent('otp_phone_not_found', { platform: 'pwa', stage, phoneLast4: phone?.slice(-4) });

  // Handle send OTP
  const handleSendOtp = async () => {
    if (!phone || isSending) return;

    setIsSending(true);
    setError('');
    logEvent('otp_sent', { platform: 'pwa', phoneLast4: phone?.slice(-4) });

    const result = await sendOtp(phone);

    if (result.error) {
      if (result.error === INVITE_REQUIRED_MESSAGE) logInviteRefusal('send');
      setError(result.error);
      setIsSending(false);
      return;
    }

    setIsSending(false);
    setResendCooldown(60);
    setStage(STAGES.CODE);

    // Focus first code input
    setTimeout(() => codeInputRefs.current[0]?.focus(), 100);
  };

  // Handle code digit input
  const handleCodeChange = (index, value) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);

    // Auto-advance to next input
    if (digit && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (digit && index === 5) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        handleVerifyOtp(fullCode);
      }
    }
  };

  // Handle backspace in code inputs
  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste into code inputs
  const handleCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 0) return;

    const newCode = [...code];
    for (let i = 0; i < pasted.length && i < 6; i++) {
      newCode[i] = pasted[i];
    }
    setCode(newCode);

    // Focus appropriate input
    if (pasted.length >= 6) {
      handleVerifyOtp(pasted.slice(0, 6));
    } else {
      codeInputRefs.current[pasted.length]?.focus();
    }
  };

  // Handle verify OTP
  const handleVerifyOtp = async (codeString) => {
    if (isVerifying) return;

    setIsVerifying(true);
    setError('');
    setStage(STAGES.LOADING);

    const result = await verifyOtp(phone, codeString);

    if (result.error) {
      logEvent('otp_failed', { error_type: result.error, phoneLast4: phone?.slice(-4) });
      setError(result.error);
      setIsVerifying(false);
      setCode(['', '', '', '', '', '']);
      setStage(STAGES.CODE);
      setTimeout(() => codeInputRefs.current[0]?.focus(), 100);
      return;
    }

    logEvent('otp_verified', { platform: 'pwa', phoneLast4: phone?.slice(-4) });

    // Hydrate data — this calls window.location.reload() on success
    const hydrationResult = await hydrateFromAuth(result.session);

    if (hydrationResult.error) {
      if (hydrationResult.error === INVITE_REQUIRED_MESSAGE) logInviteRefusal('verify');
      setError(hydrationResult.error);
      setIsVerifying(false);
      setStage(STAGES.ERROR);
    }
  };

  // Handle resend
  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError('');
    setCode(['', '', '', '', '', '']);

    setIsSending(true);
    const result = await sendOtp(phone);
    setIsSending(false);

    if (result.error) {
      setError(result.error);
      return;
    }

    setResendCooldown(60);
    setTimeout(() => codeInputRefs.current[0]?.focus(), 100);
  };

  // Handle back to phone input
  const handleBack = () => {
    setStage(STAGES.PHONE);
    setCode(['', '', '', '', '', '']);
    setError('');
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '40px 24px',
        backgroundColor: COLORS.background || '#FFFFFF',
        boxSizing: 'border-box',
      }}
    >
      {/* Logo: crown + wordmark */}
      <img
        src={CROWN_BLACK}
        alt=""
        style={{ width: '64px', height: '40px', marginBottom: '8px' }}
      />
      <div
        style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#000000',
          marginBottom: '40px',
        }}
      >
        Royale
      </div>

      {/* Phone input stage */}
      {stage === STAGES.PHONE && (
        <div
          style={{
            width: '100%',
            maxWidth: '320px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          {kickedMessage && (
            <div
              style={{
                backgroundColor: '#FFF3CD',
                border: '1px solid #FFECB5',
                borderRadius: '8px',
                padding: '12px 16px',
                textAlign: 'center',
                fontSize: '14px',
                color: '#664D03',
                width: '100%',
              }}
            >
              You&apos;ve been signed in on another device
            </div>
          )}

          <div
            style={{
              ...TYPOGRAPHY.BODY_REGULAR,
              color: COLORS.MEDIUM_GRAY,
              textAlign: 'center',
              marginBottom: '8px',
            }}
          >
            Enter your number
          </div>

          <div style={{ width: '100%' }}>
            <PhoneInputField value={phone} onChange={setPhone} />
          </div>

          {error && (
            <div style={{ fontSize: '14px', color: '#FF3B30', textAlign: 'center' }}>{error}</div>
          )}

          <ButtonRounded
            text={isSending ? 'Sending...' : 'Ok'}
            onClick={handleSendOtp}
            disabled={!phone || isSending}
            size="large"
          />
        </div>
      )}

      {/* Code entry stage */}
      {stage === STAGES.CODE && (
        <div
          style={{
            width: '100%',
            maxWidth: '320px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <div
            style={{
              ...TYPOGRAPHY.BODY_REGULAR,
              color: COLORS.MEDIUM_GRAY,
              textAlign: 'center',
              marginBottom: '4px',
            }}
          >
            Enter the code
          </div>

          {/* Six digit code inputs */}
          <div
            style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'center',
            }}
          >
            {code.map((digit, i) => (
              <input
                key={i}
                ref={(el) => (codeInputRefs.current[i] = el)}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={digit}
                onChange={(e) => handleCodeChange(i, e.target.value)}
                onKeyDown={(e) => handleCodeKeyDown(i, e)}
                onPaste={i === 0 ? handleCodePaste : undefined}
                maxLength={1}
                style={{
                  width: '46px',
                  height: '56px',
                  textAlign: 'center',
                  fontSize: '24px',
                  fontWeight: '600',
                  border: `1.5px solid ${digit ? '#333333' : '#D1D1D6'}`,
                  borderRadius: '14px',
                  outline: 'none',
                  backgroundColor: '#FFFFFF',
                  color: '#333',
                }}
              />
            ))}
          </div>

          {error && (
            <div style={{ fontSize: '14px', color: '#FF3B30', textAlign: 'center' }}>{error}</div>
          )}

          {/* Ok — auto-submit still fires on the 6th digit; this is the explicit confirm */}
          <ButtonRounded
            text="Ok"
            onClick={() => handleVerifyOtp(code.join(''))}
            disabled={code.join('').length < 6}
            size="large"
          />

          {/* Resend + Back */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '24px',
              marginTop: '8px',
            }}
          >
            <ButtonRounded text="Back" variant="blank" size="inline" onClick={handleBack} />
            <ButtonRounded
              text={resendCooldown > 0 ? `Resend (${resendCooldown}s)` : 'Resend code'}
              variant="blank"
              size="inline"
              onClick={handleResend}
              disabled={resendCooldown > 0}
            />
          </div>
        </div>
      )}

      {/* Loading stage */}
      {stage === STAGES.LOADING && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <div
            style={{
              fontSize: '18px',
              color: '#333',
            }}
          >
            Restoring your data...
          </div>
        </div>
      )}

      {/* Error stage */}
      {stage === STAGES.ERROR && (
        <div
          style={{
            width: '100%',
            maxWidth: '320px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <div
            style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#333',
              textAlign: 'center',
            }}
          >
            {error === INVITE_REQUIRED_MESSAGE ? INVITE_REQUIRED_MESSAGE : 'Something went wrong'}
          </div>

          {error !== INVITE_REQUIRED_MESSAGE && (
            <div style={{ fontSize: '14px', color: '#666', textAlign: 'center' }}>{error}</div>
          )}

          <ButtonRounded text="Try again" onClick={handleBack} size="large" />
        </div>
      )}
    </div>
  );
};

export default PWAAuthScreen;
